/**
 * @file PostItem.jsx
 * @description This component renders a news item card with user interactions such as like, comment, and share.
 * It also handles displaying comments and replies to comments.
 * @module NewsItem
 */
import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Heart,
	MessageCircle,
	Share2,
	MoreVertical,
	Trash2,
	AlertTriangle,
	Send,
	Star,
	Trophy,
	Mail,
	MessageSquare,
	Twitter,
	Linkedin,
	Copy,
	Image,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from "@/components/ui/dialog";
import CommentsSection from "./CommentsSection";
import PostTypeBadge from "./PostTypeBadge";
import { getYouTubeEmbedUrl } from "../../js/utils/mediaUtils";
import { useNavigate } from "react-router-dom";
import useNewsfeedStore from "../../zustand/UseNewsfeed";
import useUserStore from "../../zustand/UseUserStore";
import useAuthRedirect from "../../hooks/useAuthRedirect";
import { showErrorToast } from "../../js/utils/errors";
import { showSuccessToast } from "../../js/utils/toast";
import { IMAGE_PATH } from "../../api/apiConfig";
import AudioPostPlayer from "./AudioPostPlayer";
import { getPostId, detectPostType } from "../../utils/postTypeUtils";
import { FacebookShareButton } from "react-share";
import { useCookieConsent } from "../../context/useCookieConsent";

const PostItem = ({
	item,
	lastItemRef,
	onPostSelected,
	isCompact = true,
	showComments = true,
	selectedCommentsPost,
	setSelectedCommentsPost,
	onSendLumens,
	currentContestFilter = 0,
	loadAudioImmediately = false,
}) => {
	const imagePath = IMAGE_PATH;

	const itemId = getPostId(item);
	const postType = detectPostType(item);
	const shareArrow = `${imagePath}/icon/shareArrow.svg`;

	const navigate = useNavigate();
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [shareItemId, setShareItemId] = useState(-1);

	const newsfeed = useNewsfeedStore();
	const userStore = useUserStore();
	const authRedirect = useAuthRedirect();
	const { withCookieCheck } = useCookieConsent();

	const isOwnPost = item.MemberID === userStore.user.MemberID;
	const likeIndex = newsfeed.likedItems.indexOf(itemId);
	const isCommenting = isCompact
		? newsfeed.isCommenting.includes(itemId)
		: true;

	// Handle post deletion with API call
	const handleDeletePost = () => {
		setIsDeleting(true);
		setTimeout(() => (document.body.style.pointerEvents = ""), 300);
		try {
			newsfeed.handleDelete(itemId);
		} catch (error) {
			console.error("Failed to delete post:", error);
		} finally {
			setIsDeleteDialogOpen(false);
			setIsDeleting(false);
		}
	};

	// Determine if this is a "glow" post (high lumens)
	// Only show glow badge for actual glow post types (not pride or grateful posts)
	const isGlowPost = postType === "glow" && item.Lumens > 0;
	const glowType = item.Type;
	console.log("Post debug:", {
		GlowID: item.GlowID,
		Lumens: item.Lumens,
		Score: item.Score,
		ContestID: item.ContestID,
		postType,
		isGlowPost,
		hasPoints: item.Score !== undefined && item.Score !== null,
		pointsValue: item.Score,
	});

	return (
		<Card
			ref={lastItemRef}
			className={`relative overflow-hidden border-2 ${
				isGlowPost
					? "border-yellow-500 bg-yellow-50 shadow-lg dark:border-yellow-500 dark:bg-yellow-900/20"
					: postType === "pride"
						? "border-purple-500 bg-purple-50 shadow-lg dark:border-purple-500 dark:bg-purple-900/20"
						: postType === "grateful"
							? "border-green-500 bg-green-50 shadow-lg dark:border-green-500 dark:bg-green-900/20"
							: "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
			}`}
		>
			{/* Instagram-style Header: Avatar and Name at Top - Only show for non-video posts */}
			{item.MediaURL && item.Format === "video" ? null : (
				<CardHeader className="min-w-0 px-3 py-2">
					<div className="flex items-center justify-between">
						<div className="flex min-w-0 flex-1 items-center gap-3">
							<Avatar className="h-10 w-10 flex-shrink-0 border border-border dark:border-border">
								<AvatarFallback className="bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground">
									{item.AuthorName}
								</AvatarFallback>
								<AvatarImage
									src={
										item.AvatarURL ||
										"https://test.goodglows.com/static/goodglows/images/avatars/default.svg"
									}
								/>
							</Avatar>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-2">
									<p className="text-sm font-semibold text-foreground dark:text-foreground">
										{item.AuthorName}
									</p>
									<PostTypeBadge
										postType={postType}
										isGlowPost={isGlowPost}
										variant="default"
									/>
								</div>
								<CardDescription className="text-xs text-muted-foreground dark:text-muted-foreground">
									{new Date(item.CreatedAt).toLocaleDateString()}
								</CardDescription>
							</div>
						</div>
						{/* Temporarily disabled - no menu items available */}
						{/* {isOwnPost && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 rounded-full text-muted-foreground dark:text-muted-foreground hover:bg-muted/50 dark:hover:bg-muted/50"
							>
								<MoreVertical className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							className="bg-popover dark:bg-popover border-border dark:border-border"
						>
							<DropdownMenuItem
								className="text-red-500 focus:text-red-500 hover:bg-muted/50 dark:hover:bg-muted/50"
								onClick={(e) => {
									e.stopPropagation();
									setIsDeleteDialogOpen(true);
								}}
								disabled={isDeleting}
							>
								<Trash2 className="h-4 w-4 mr-2" />
								Delete post
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)} */}
					</div>
				</CardHeader>
			)}

			{/* Instagram-style Media: Full width, prominent placement */}
			<div
				className={`relative w-full overflow-hidden border-y border-border dark:border-border ${isCompact ? "cursor-pointer" : ""}`}
				onClick={() => {
					if (isCompact) {
						onPostSelected(item);
						navigate(`/newsfeed/${postType}/${itemId}`);
					}
				}}
			>
				{item.MediaURL ? (
					item.Format === "video" ? (
						item.MediaURL.includes("youtube.com") ||
						item.MediaURL.includes("youtu.be") ? (
							// Handle YouTube videos with iframe embed
							<div className="relative aspect-video w-full">
								<iframe
									src={getYouTubeEmbedUrl(item.MediaURL, 60)}
									title={item.Title}
									className="h-full w-full"
									frameBorder="0"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
								/>
								{/* Overlay with avatar, username, date, and tag - positioned on top of video */}
								<div className="pointer-events-none absolute left-0 right-0 top-0 z-20 p-3">
									<div className="pointer-events-auto flex max-w-fit items-center gap-3 rounded-lg px-3 py-2">
										<Avatar className="h-10 w-10 flex-shrink-0 border-2 border-white/50">
											<AvatarFallback className="bg-white/20 text-white">
												{item.AuthorName}
											</AvatarFallback>
											<AvatarImage
												src={
													item.AvatarURL ||
													"https://test.goodglows.com/static/goodglows/images/avatars/default.svg"
												}
											/>
										</Avatar>
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-2">
												<p className="text-sm font-semibold text-white drop-shadow-lg">
													{item.AuthorName}
												</p>
												<PostTypeBadge
													postType={postType}
													isGlowPost={isGlowPost}
													variant="overlay"
												/>
											</div>
											<p className="text-xs text-white/90 drop-shadow-md">
												{new Date(item.CreatedAt).toLocaleDateString()}
											</p>
										</div>
									</div>
								</div>
							</div>
						) : (
							// Handle direct video files
							<div className="relative w-full">
								<video
									src={item.MediaURL}
									controls
									autoPlay
									playsInline
									loop
									muted
									className="h-auto max-h-[600px] w-full object-cover"
								/>
								{/* Overlay with avatar, username, date, and tag - positioned on top of video */}
								<div className="pointer-events-none absolute left-0 right-0 top-0 z-20 p-3">
									<div className="pointer-events-auto flex items-center gap-3 rounded-lg px-3 py-2">
										<Avatar className="h-10 w-10 flex-shrink-0 border-2 border-white/50">
											<AvatarFallback className="bg-white/20 text-white">
												{item.AuthorName}
											</AvatarFallback>
											<AvatarImage
												src={
													item.AvatarURL ||
													"https://test.goodglows.com/static/goodglows/images/avatars/default.svg"
												}
											/>
										</Avatar>
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-2">
												<p className="text-sm font-semibold text-white drop-shadow-lg">
													{item.AuthorName}
												</p>
												<PostTypeBadge
													postType={postType}
													isGlowPost={isGlowPost}
													variant="overlay"
												/>
											</div>
											<p className="text-xs text-white/90 drop-shadow-md">
												{new Date(item.CreatedAt).toLocaleDateString()}
											</p>
										</div>
									</div>
								</div>
							</div>
						)
					) : item.Format === "audio" ? (
						// Handle audio files with waveform and custom player - full width
						<AudioPostPlayer
							audioUrl={item.MediaURL}
							waveformData={item.waveformdata || item.WaveformData || null}
							onClick={(e) => e.stopPropagation()}
							loadImmediately={loadAudioImmediately}
						/>
					) : (
						// Square image format to match crop size (480x480 proportionally)
						<img
							src={item.MediaURL}
							alt={item.Title || "Post image"}
							className="aspect-square w-full object-cover"
						/>
					)
				) : (
					// Placeholder for posts without images - square format (same as crop size proportionally)
					<div className="flex aspect-square w-full items-center justify-center bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-800 dark:to-gray-700">
						<div className="flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
							<Star size={48} className="opacity-50" />
							<span className="text-sm font-medium opacity-75">No Image</span>
						</div>
					</div>
				)}
			</div>

			{/* Instagram-style Content: Description below media (no title) */}
			<CardContent className="min-w-0 px-3 py-2">
				<div
					className={isCompact ? "cursor-pointer" : ""}
					onClick={() => {
						if (isCompact) {
							onPostSelected(item);
							navigate(`/newsfeed/${postType}/${itemId}`);
						}
					}}
				>
					{item.Glow || item.Description ? (
						<p className="break-words text-sm text-muted-foreground dark:text-muted-foreground">
							{item.Glow ? item.Glow : item.Description}
						</p>
					) : null}
					{item.Transcription ? (
						<p className="mt-2 text-xs text-gray-500">
							Transcription: {item.Transcription}
						</p>
					) : null}
				</div>
			</CardContent>
			{glowType !== "StarSaluteSignup" && (
				<CardFooter className="flex w-full min-w-0 flex-col px-3 py-2">
					<div className="flex w-full min-w-0 items-center justify-between gap-2">
						<div className="flex min-w-0 flex-1 gap-2 sm:gap-4">
							<Button
								variant="ghost"
								size="sm"
								className="flex items-center gap-1 px-2 text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
								onClick={withCookieCheck((e) => {
									e.stopPropagation();
									if (!userStore.userProgress.isAuthenticated) {
										authRedirect();
										return;
									}
									newsfeed.handleLike(itemId);
								})}
							>
								<Heart
									className={`h-4 w-4 transition-colors duration-200 ${
										likeIndex !== -1 ? "fill-red-500 text-red-500" : ""
									}`}
								/>
								<span className="text-sm sm:text-base">{item.LikesCount}</span>
							</Button>
							{isCompact && showComments ? (
								<Button
									variant={isCommenting ? "default" : "ghost"}
									size="sm"
									className={`flex items-center gap-1 px-2 ${
										isCommenting
											? "bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700"
											: "text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
									}`}
									onClick={withCookieCheck((e) => {
										e.stopPropagation();
										newsfeed.onComment(itemId);
									})}
								>
									<MessageCircle className="h-4 w-4" />
									<span className="text-sm sm:text-base">
										{item.CommentsCount}
									</span>
								</Button>
							) : null}

							<Button
								variant="ghost"
								size="sm"
								className="flex items-center gap-1 px-2 text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
								onClick={withCookieCheck(async (e) => {
									e.stopPropagation();
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									const shareText =
										item.Glow ||
										item.Description ||
										item.Title ||
										"Check out this glow!";
									const response = await fetch(item.MediaURL);
									const blob = await response.blob();
									const file = new File([blob], "image.jpg", {
										type: "image/jpeg",
									});
									const dataTransfer = new DataTransfer();
									const isMobile = /iPhone|iPad|iPod|Android/i.test(
										navigator.userAgent
									);
									dataTransfer.items.add(file);
									if (navigator.share && isMobile) {
										try {
											const shareData = {
												title: shareText,
												text: shareText,
												url: shareUrl,
											};
											await navigator.share(shareData);
											// Track the share after successful sharing (skip the share call since we already did it)
											newsfeed.handleShare(itemId, shareText, true);
										} catch (error) {
											// User cancelled or error occurred
											if (error.name !== "AbortError") {
												console.error("Error sharing:", error);
											}
										}
									} else {
										setShareItemId(itemId);
									}
									//setShareItemId(itemId);
								})}
							>
								<img
									src={shareArrow}
									alt="Share"
									className="h-4 w-4 opacity-60 dark:opacity-80 dark:invert"
								/>
								<span className="text-sm sm:text-base">{item.SharesCount}</span>
							</Button>

							{/* Send Lumens Button - icon only on mobile, text on larger screens */}
							{onSendLumens ? (
								<Button
									variant="ghost"
									size="sm"
									className="flex items-center gap-1 rounded-lg px-2 py-2 text-gray-600 transition-colors hover:bg-yellow-500/20 hover:text-yellow-600 dark:text-gray-400 sm:px-3"
									onClick={withCookieCheck((e) => {
										e.stopPropagation();
										if (!userStore.userProgress.isAuthenticated) {
											authRedirect();
											return;
										}
										onSendLumens(itemId);
									})}
								>
									<Send className="h-4 w-4" />
									<span className="hidden text-xs sm:inline sm:text-sm">
										Send Lumens
									</span>
								</Button>
							) : null}
						</div>

						{/* Lumens and Points count - bottom right */}
						<div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-3">
							{/* Show Points only when viewing a contest feed (not Glow Game) and score exists */}
							{currentContestFilter > 0 &&
								item.Score !== undefined &&
								item.Score !== null && (
									<div className="flex items-center gap-1 text-xs font-bold text-purple-600 dark:text-purple-400 sm:text-sm">
										<span>{item.Score}</span>
										<Trophy className="h-4 w-4" />
									</div>
								)}
							{/* Lumens count */}
							<div className="flex items-center gap-1 text-xs font-bold text-yellow-600 dark:text-yellow-500 sm:text-sm">
								<span>{item.Lumens || 0}</span>
								<Star className="h-4 w-4" />
							</div>
						</div>
					</div>

					{/* Comment section - only shown when commenting on this item and showComments is true */}
					<AnimatePresence mode="wait">
						{showComments && isCommenting ? (
							<CommentsSection
								key={`comments-${itemId}`}
								itemId={itemId}
								isCompact={isCompact}
								isOwnPost={isOwnPost}
								postType={postType}
							/>
						) : null}
					</AnimatePresence>
				</CardFooter>
			)}
			<Dialog
				open={shareItemId !== -1}
				onOpenChange={() => {
					if (shareItemId !== -1) {
						setShareItemId(-1);
					}
				}}
			>
				<DialogContent
					className="border-border bg-background dark:border-border dark:bg-background sm:max-w-[500px]"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<DialogHeader>
						<DialogTitle className="text-foreground dark:text-foreground">
							Share Glow
						</DialogTitle>
						<DialogDescription className="text-center text-muted-foreground dark:text-muted-foreground">
							Share a link to this glow's page
						</DialogDescription>
					</DialogHeader>

					<div className="w-full overflow-auto">
						<div className="grid grid-cols-3 gap-4 p-4 sm:grid-cols-4">
							{/* Copy Link */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-primary hover:bg-primary/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-primary dark:hover:bg-primary/5"
								onClick={async () => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									try {
										await navigator.clipboard.writeText(shareUrl);
										showSuccessToast("Link copied!", "Share it anywhere!");
										newsfeed.handleShare(itemId, item.Title);
									} catch (err) {
										showErrorToast("Failed to copy link");
									}
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground dark:bg-primary/10 dark:text-primary dark:group-hover:bg-primary dark:group-hover:text-primary-foreground">
									<Copy className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									Copy Link
								</span>
							</button>

							{/* SMS */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-green-500 hover:bg-green-500/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-green-500 dark:hover:bg-green-500/5"
								onClick={() => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									const shareText = item.Title || "Check out this glow!";
									const smsUrl = `sms:?body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
									window.location.href = smsUrl;
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10 text-green-600 transition-colors group-hover:bg-green-500 group-hover:text-white dark:bg-green-500/10 dark:text-green-400 dark:group-hover:bg-green-500 dark:group-hover:text-white">
									<MessageSquare className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									SMS
								</span>
							</button>

							{/* Email */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-blue-500 hover:bg-blue-500/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-blue-500 dark:hover:bg-blue-500/5"
								onClick={() => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									const shareText = item.Title || "Check out this glow!";
									const emailUrl = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`Check out this glow: ${shareUrl}`)}`;
									window.location.href = emailUrl;
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10 text-blue-600 transition-colors group-hover:bg-blue-500 group-hover:text-white dark:bg-blue-500/10 dark:text-blue-400 dark:group-hover:bg-blue-500 dark:group-hover:text-white">
									<Mail className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									Email
								</span>
							</button>

							{/* X (Twitter) */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-[#1DA1F2] hover:bg-[#1DA1F2]/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-[#1DA1F2] dark:hover:bg-[#1DA1F2]/5"
								onClick={() => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									const shareText = item.Description
										? `${item.Description.slice(0, 200)}...`
										: "Check out this glow!";
									const twitterUrl = `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
									const isMobile = /iPhone|iPad|iPod|Android/i.test(
										navigator.userAgent
									);
									if (isMobile) {
										window.location.href = twitterUrl;
									} else {
										window.open(twitterUrl, "_blank", "width=550,height=420");
									}
									newsfeed.handleShare(itemId, item.Title, true);
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1DA1F2]/10 text-[#1DA1F2] transition-colors group-hover:bg-[#1DA1F2] group-hover:text-white dark:bg-[#1DA1F2]/10 dark:text-[#1DA1F2] dark:group-hover:bg-[#1DA1F2] dark:group-hover:text-white">
									<Twitter className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									X
								</span>
							</button>

							{/* Facebook */}
							<FacebookShareButton
								url={`${window.location.origin}/newsfeed/${postType}/${itemId}`}
								quote={item.Title || "Check out this glow!"}
								onShareWindowClose={() => {
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								<button
									className="group flex w-full flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-[#1877F2] hover:bg-[#1877F2]/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-[#1877F2] dark:hover:bg-[#1877F2]/5"
									type="button"
								>
									<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1877F2]/10 text-[#1877F2] transition-colors group-hover:bg-[#1877F2] group-hover:text-white dark:bg-[#1877F2]/10 dark:text-[#1877F2] dark:group-hover:bg-[#1877F2] dark:group-hover:text-white">
										<svg
											className="h-5 w-5 fill-current"
											viewBox="0 0 24 24"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
										</svg>
									</div>
									<span className="text-xs font-medium text-foreground dark:text-foreground">
										Facebook
									</span>
								</button>
							</FacebookShareButton>

							{/* Instagram */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-pink-500 hover:bg-pink-500/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-pink-500 dark:hover:bg-pink-500/5"
								onClick={async () => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									try {
										await navigator.clipboard.writeText(shareUrl);
										showSuccessToast(
											"Link copied!",
											"Paste it in your Instagram story or post."
										);
										newsfeed.handleShare(itemId, item.Title);
									} catch (err) {
										showErrorToast("Failed to copy link");
									}
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] transition-transform group-hover:scale-110">
									<Image className="h-5 w-5 text-white" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									Instagram
								</span>
							</button>

							{/* Threads */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-gray-800 hover:bg-gray-800/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-gray-300 dark:hover:bg-gray-300/5"
								onClick={async () => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									try {
										await navigator.clipboard.writeText(shareUrl);
										showSuccessToast(
											"Link copied!",
											"Paste it in your Threads post."
										);
										newsfeed.handleShare(itemId, item.Title);
									} catch (err) {
										showErrorToast("Failed to copy link");
									}
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-800/10 text-gray-800 transition-colors group-hover:bg-gray-800 group-hover:text-white dark:bg-gray-300/10 dark:text-gray-300 dark:group-hover:bg-gray-300 dark:group-hover:text-gray-900">
									<MessageCircle className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									Threads
								</span>
							</button>

							{/* Snapchat */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-[#FFFC00] hover:bg-[#FFFC00]/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-[#FFFC00] dark:hover:bg-[#FFFC00]/5"
								onClick={async () => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									try {
										await navigator.clipboard.writeText(shareUrl);
										showSuccessToast(
											"Link copied!",
											"Paste it in your Snapchat story or chat."
										);
										newsfeed.handleShare(itemId, item.Title);
									} catch (err) {
										showErrorToast("Failed to copy link");
									}
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFFC00]/20 text-[#FFFC00] transition-colors group-hover:bg-[#FFFC00] group-hover:text-gray-900 dark:bg-[#FFFC00]/20 dark:text-[#FFFC00] dark:group-hover:bg-[#FFFC00] dark:group-hover:text-gray-900">
									<MessageSquare className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									Snapchat
								</span>
							</button>

							{/* LinkedIn */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-[#0077B5] hover:bg-[#0077B5]/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-[#0077B5] dark:hover:bg-[#0077B5]/5"
								onClick={() => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
									const isMobile = /iPhone|iPad|iPod|Android/i.test(
										navigator.userAgent
									);
									if (isMobile) {
										window.location.href = linkedInUrl;
									} else {
										window.open(linkedInUrl, "_blank", "width=550,height=420");
									}
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0077B5]/10 text-[#0077B5] transition-colors group-hover:bg-[#0077B5] group-hover:text-white dark:bg-[#0077B5]/10 dark:text-[#0077B5] dark:group-hover:bg-[#0077B5] dark:group-hover:text-white">
									<Linkedin className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									LinkedIn
								</span>
							</button>

							{/* BlueSky */}
							<button
								className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:scale-105 hover:border-[#0085FF] hover:bg-[#0085FF]/5 hover:shadow-md dark:border-border dark:bg-card dark:hover:border-[#0085FF] dark:hover:bg-[#0085FF]/5"
								onClick={() => {
									const shareUrl = `${window.location.origin}/newsfeed/${postType}/${itemId}`;
									const shareText = item.Title || "Check out this glow!";
									const blueSkyUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
									const isMobile = /iPhone|iPad|iPod|Android/i.test(
										navigator.userAgent
									);
									if (isMobile) {
										window.location.href = blueSkyUrl;
									} else {
										window.open(blueSkyUrl, "_blank", "width=550,height=420");
									}
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0085FF]/10 text-[#0085FF] transition-colors group-hover:bg-[#0085FF] group-hover:text-white dark:bg-[#0085FF]/10 dark:text-[#0085FF] dark:group-hover:bg-[#0085FF] dark:group-hover:text-white">
									<Share2 className="h-5 w-5" />
								</div>
								<span className="text-xs font-medium text-foreground dark:text-foreground">
									BlueSky
								</span>
							</button>
						</div>
					</div>

					<DialogFooter>
						<DialogClose asChild>
							<Button
								variant="outline"
								className="border-border text-foreground hover:bg-muted/50 dark:border-border dark:text-foreground dark:hover:bg-muted/50"
							>
								Cancel
							</Button>
						</DialogClose>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Temporarily disabled delete functionality */}
			{/* Delete Confirmation Dialog */}
			{/* <AlertDialog
			open={isDeleteDialogOpen}
			onOpenChange={setIsDeleteDialogOpen}
			onInteractOutside={(e) => {
				e.stopPropagation();
				e.preventDefault();
			}}
		>
			<AlertDialogContent
				onClick={(e) => {
					e.stopPropagation();
				}}
				onInteractOutside={(e) => {
					e.stopPropagation();
					e.preventDefault();
				}}
				className="bg-background dark:bg-background border-border dark:border-border"
			>
				<AlertDialogHeader>
					<AlertDialogTitle className="flex items-center gap-2 text-foreground dark:text-foreground">
						<AlertTriangle className="h-5 w-5 text-red-500" />
						Delete Post
					</AlertDialogTitle>
					<AlertDialogDescription className="text-muted-foreground dark:text-muted-foreground">
						Are you sure you want to delete this post? This action cannot be
						undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel
						onClick={(e) => {
							e.stopPropagation();
							setTimeout(() => (document.body.style.pointerEvents = ""), 300);
						}}
						disabled={isDeleting}
						className="border-border dark:border-border text-foreground dark:text-foreground hover:bg-muted/50 dark:hover:bg-muted/50"
					>
						Cancel
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={(e) => {
							e.stopPropagation();
							handleDeletePost();
						}}
						disabled={isDeleting}
						className="bg-red-500 hover:bg-red-600 text-white"
					>
						{isDeleting ? "Deleting..." : "Delete"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog> */}
		</Card>
	);
};

export default PostItem;
